import React from 'react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { useNokkeltallSammendrag } from '../kostnadskalkyle/context/useNokkeltallSammendrag';

import { FormikNumericFormat } from '@klp/formgenerator/components/FormikNumericFormat';
import { useTranslation } from 'react-i18next';
import {
    handleFormikNumericFieldBlur,
    handleFormikNumericFieldChange,
} from '@forvalter/utils';
import { useField, useFormikContext } from 'formik';
import type { KostnadskjemaSummary } from '@klp/prosjekt/services/types';
import { useKostnadsKalkyleContext } from '../kostnadskalkyle/context/KostnadsKalkyleProvider';

const TERMINER_OPTIONS = [1, 2, 3, 4, 6, 12];
export const LonnsomhetGeneralData = () => {
    const { t } = useTranslation();
    const { nokkeltall } = useNokkeltallSammendrag();
    const { isReadOnly } = useKostnadsKalkyleContext();
    const { handleChange } = useFormikContext<KostnadskjemaSummary>();
    const [terminerField] = useField<number>('terminerPerAar');

    return (
        <Grid container={true} spacing={3}>
            <Grid item={true} xs={12}>
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="leieobjektArealM2"
                            label={t(
                                'prosjekt.kostnadskalkyle.leieobjektArealM2',
                            )}
                            variant="outlined"
                            align="right"
                            disabled={true}
                        />
                    </Grid>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="kontraktsleiePerM2"
                            label={t(
                                'prosjekt.kostnadskalkyle.kontraktsleiePerM2',
                            )}
                            variant="outlined"
                            align="right"
                            disabled={isReadOnly}
                            onChange={(e) => {
                                handleFormikNumericFieldChange(
                                    handleChange,
                                    e,
                                    `kontraktsleiePerM2`,
                                );
                            }}
                            onFocus={(e) => e.target.select()}
                            onBlur={(e) => {
                                handleFormikNumericFieldBlur(e, handleChange);
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="leieperiode"
                            label={t('prosjekt.kostnadskalkyle.leieperiode')}
                            variant="outlined"
                            align="right"
                            disabled={isReadOnly}
                            onChange={(e) => {
                                handleFormikNumericFieldChange(
                                    handleChange,
                                    e,
                                    `leieperiode`,
                                );
                            }}
                            onFocus={(e) => e.target.select()}
                            onBlur={(e) => {
                                handleFormikNumericFieldBlur(e, handleChange);
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="kontraktleieTotaltPerAar"
                            label={t('prosjekt.kostnadskalkyle.aarligLeie')}
                            value={nokkeltall?.kontraktleieTotaltPerAar ?? 0}
                            variant="outlined"
                            align="right"
                            disabled={true}
                        />
                    </Grid>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="totalLeie"
                            label={t('prosjekt.kostnadskalkyle.totalLeie')}
                            value={nokkeltall?.totalLeie ?? 0}
                            variant="outlined"
                            align="right"
                            disabled={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item={true} xs={12}>
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="avkastningskrav"
                            label={t(
                                'prosjekt.lonnsomhet.fields.avkastningskrav',
                            )}
                            variant="outlined"
                            align="right"
                            allowNegative={false}
                            allowLeadingZeros={false}
                            disabled={isReadOnly}
                            onChange={(e) => {
                                handleFormikNumericFieldChange(
                                    handleChange,
                                    e,
                                    `avkastningskrav`,
                                );
                            }}
                            onFocus={(e) => e.target.select()}
                            onBlur={(e) => {
                                handleFormikNumericFieldBlur(e, handleChange);
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="inflasjon"
                            label={t('prosjekt.lonnsomhet.fields.inflasjon')}
                            variant="outlined"
                            align="right"
                            allowLeadingZeros={false}
                            disabled={isReadOnly}
                            onChange={(e) => {
                                handleFormikNumericFieldChange(
                                    handleChange,
                                    e,
                                    `inflasjon`,
                                );
                            }}
                            onFocus={(e) => e.target.select()}
                            onBlur={(e) => {
                                handleFormikNumericFieldBlur(e, handleChange);
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={6} sm={2}>
                        <FormikNumericFormat
                            id="realAvkastningskrav"
                            label={t(
                                'prosjekt.lonnsomhet.fields.realAvkastningskrav',
                            )}
                            value={nokkeltall?.realAvkastningskrav ?? 0}
                            variant="outlined"
                            disabled={true}
                            align="right"
                            error={
                                nokkeltall?.realAvkastningskrav !== undefined &&
                                nokkeltall.realAvkastningskrav < 0
                            }
                        />
                    </Grid>
                    <Grid item={true} xs={6} sm={4} />
                    <Grid item={true} xs={6} sm={2}>
                        <TextField
                            select={true}
                            id="terminerPerAar"
                            name="terminerPerAar"
                            label={t(
                                'prosjekt.lonnsomhet.fields.terminerPerAar',
                            )}
                            variant="outlined"
                            fullWidth={true}
                            value={terminerField.value}
                            disabled={isReadOnly}
                            onChange={handleChange}
                        >
                            {TERMINER_OPTIONS.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
